import React, { type FC, useState, useEffect, useRef } from 'react';
import { Typography, Link, Button, CircularProgress } from '@mui/material';
import { isEmpty, values } from 'ramda';
import { type UseFormReturn, type FieldError } from 'react-hook-form';

import Div from '../../shared/ui/Div';
import ContentLayout from '../../shared/ui/ContentLayout';
import { LessonGoBackLine, LingoLiftAssign, MainLayout } from '../../widgets';
import {
  type ILingoLiftLessonAssignment,
  type ILingoLiftManageClass,
  useGetLingoLiftManageLLClassesListQuery,
  useGetLingoLiftTeacherLessonAssignmentSchoolClassesQuery,
  usePatchLingoLiftTeacherLessonAddAssignmentSchoolClassesMutation,
} from '../../entities/lingoliftClasses';
import { UserRole } from '../../entities/user';
import { useSnackbar } from '../../shared/hooks/useSnackbar';

import styles from './styles';

interface ILingoLiftLessonAssignPageProps {
  id: number;
  schoolClassesLink: string;
}

interface FormFieldData {
  value: string | number | boolean;
  isValid?: boolean;
}

type FormData = Record<string, FormFieldData>;

interface ErrorResponse {
  data?: {
    errorMessage?: string;
  };
}

interface ApiError {
  message?: string;
}

const LingoLiftLessonAssignPage: FC<ILingoLiftLessonAssignPageProps> = props => {
  const { id, schoolClassesLink } = props;

  const [records, setRecords] = useState<ILingoLiftLessonAssignment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formRefs = useRef<Record<number, UseFormReturn<FormData>>>({});

  const registerForm = (classId: number, formInstance: UseFormReturn<FormData>) => {
    formRefs.current[classId] = formInstance;
  };

  const { showSuccessNotification, showErrorNotification } = useSnackbar();

  const { data: lessonAssignment, isFetching: isLessonAssignmentLoading } =
    useGetLingoLiftTeacherLessonAssignmentSchoolClassesQuery({ llLessonId: id });

  const { data: llClassesList, isLoading: isLlClassesLoading } =
    useGetLingoLiftManageLLClassesListQuery();

  const [
    patchLingoLiftTeacherLessonAddAssignmentSchoolClasses,
    { isLoading: isAddLessonAssignmentLoading, error: addLessonAssignmentError },
  ] = usePatchLingoLiftTeacherLessonAddAssignmentSchoolClassesMutation();

  const handleChange = (data: ILingoLiftLessonAssignment) => {
    const existingRecord = records.find(record => record.llSchoolClassId === data.llSchoolClassId);
    if (existingRecord) {
      setRecords(
        records.map(record => (record.llSchoolClassId === data.llSchoolClassId ? data : record)),
      );
    } else {
      setRecords([...records, data]);
    }
  };

  useEffect(() => {
    if (lessonAssignment?.items) {
      setRecords(lessonAssignment.items);
    }
  }, [lessonAssignment]);

  const handleSubmit = async () => {
    const allRecords = records ?? [];
    const activeRecords = allRecords?.filter(record => record.isActive) ?? [];

    const validationPromises = activeRecords.map(async record => {
      const form = formRefs.current[record.llSchoolClassId];
      if (form) {
        const isValid = await form.trigger();
        if (!isValid) {
          return values(form.formState.errors).map((error: FieldError) => error.message ?? '');
        }
      }
      return [];
    });

    const errorArrays = await Promise.all(validationPromises);
    const errors = errorArrays.flat().filter((error): error is string => Boolean(error));

    if (isEmpty(errors)) {
      setIsSubmitting(true);
      try {
        const response = await patchLingoLiftTeacherLessonAddAssignmentSchoolClasses({
          params: { llLessonId: id },
          body: { llLessonAssignmentSchoolClasses: allRecords },
        }).unwrap();
        showSuccessNotification(response.successMessage);
        if (lessonAssignment?.llLessonUrl) {
          setTimeout(() => {
            window.location.href = lessonAssignment?.llLessonUrl;
          }, 1000);
        }
      } catch (error: unknown) {
        setIsSubmitting(false);
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError?.data?.errorMessage ??
          (addLessonAssignmentError as ApiError)?.message ??
          'An error occurred';
        showErrorNotification(errorMessage);
      }
    } else {
      errors.forEach(error => showErrorNotification(error));
    }
  };

  const isLoading = isLessonAssignmentLoading || isLlClassesLoading || isAddLessonAssignmentLoading;

  const renderBottomActions = () => {
    const hasClasses = llClassesList?.items && llClassesList.items.length > 0;

    if (hasClasses) {
      return (
        <Div sx={styles.bottomActions}>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isLoading || isSubmitting}
            sx={{
              '&.Mui-disabled': {
                color: 'white',
                opacity: 0.8
              },
            }}
            endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Assigning...' : 'Assign'}
          </Button>
        </Div>
      );
    }
    return (
      <Div sx={styles.noClassesMessageWrapper}>
        <Typography variant="h4">You haven't created any Lingolift classes yet.</Typography>
        <Typography>
          To assign lessons to students, please{' '}
          <Link href={schoolClassesLink} sx={styles.addSchoolClassLink}>create a Lingolift class</Link> first.
        </Typography>
      </Div>
    );
  };

  return (
    <MainLayout
      backgroundColor="athensGray"
      isLoading={isLlClassesLoading || isLessonAssignmentLoading}
      userRole={UserRole.Teacher}
    >
      <LessonGoBackLine title="Back to Lesson" />
      <ContentLayout>
        <Div sx={styles.info}>
          <Div sx={styles.announce}>
            <Typography sx={styles.title} variant="h1">
              Assign Lesson
            </Typography>
            <Button component={Link} href={lessonAssignment?.llLessonUrl} sx={styles.previewButton}>
              Preview Lesson
            </Button>
          </Div>
          <Div sx={styles.description}>
            <Typography sx={styles.descriptionParagraph} variant="h4" fontWeight={400}>
              You're assigning the video:
            </Typography>
            <Typography sx={styles.descriptionParagraph} variant="h4" color="primary.dark">
              {lessonAssignment?.llLessonName}
            </Typography>
          </Div>
        </Div>
        {llClassesList?.items.map((llClass: ILingoLiftManageClass) => (
          <LingoLiftAssign
            key={llClass.id}
            llClass={llClass}
            assigments={lessonAssignment?.items ?? []}
            handleChange={handleChange}
            fieldName="lessonAssignment"
            lessonId={id}
            registerForm={form => registerForm(llClass.id, form)}
            disabled={isSubmitting}
          />
        ))}
        {renderBottomActions()}
      </ContentLayout>
    </MainLayout>
  );
};

export default LingoLiftLessonAssignPage;
